import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SimilarIssue} from '@github-ui/issue-create/SimilarIssues'
import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {ISSUE_VIEWER_DEFAULT_CONFIG} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {noop} from '@github-ui/noop'
import {ScreenFullIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useCallback, useState} from 'react'

import {LABELS} from '../constants/labels'
import {IssueSidePanel} from './show/IssueSidePanel'

type SimilarIssueSidePanelProps = {
  repository: {owner: string; name: string} | undefined
  children: (onSimilarIssueClick?: (issue: SimilarIssue) => void) => React.ReactNode
}

// No-op handler when feature is disabled - ensures stable reference
const noopHandler = undefined

/**
 * Wraps children with side panel functionality for previewing similar issues during issue creation.
 * Colocates the side panel state and rendering, exposing only the click handler to children.
 *
 * Uses a render prop pattern so consumers can pass the handler to IssueCreatePage.
 * When the feature flag is disabled, passes undefined to children and skips side panel rendering.
 */
export function SimilarIssueSidePanel({repository, children}: SimilarIssueSidePanelProps) {
  const isEnabled = isFeatureEnabled('semantic_similarity_duplicate_issue_detection')
  const [sidePanelItemIdentifier, setSidePanelItemIdentifier] = useState<ItemIdentifier | null>(null)

  const onSimilarIssueClick = useCallback(
    (issue: SimilarIssue) => {
      if (!repository) return
      setSidePanelItemIdentifier({
        owner: repository.owner,
        repo: repository.name,
        number: issue.number,
        type: 'Issue',
      })
    },
    [repository],
  )

  const onClose = useCallback(() => {
    setSidePanelItemIdentifier(null)
  }, [])

  // When feature is disabled, just render children without side panel functionality
  if (!isEnabled) {
    return <>{children(noopHandler)}</>
  }

  const sidePanelItemURL = sidePanelItemIdentifier
    ? `/${sidePanelItemIdentifier.owner}/${sidePanelItemIdentifier.repo}/issues/${sidePanelItemIdentifier.number}`
    : undefined

  return (
    <>
      {children(onSimilarIssueClick)}
      {sidePanelItemIdentifier && (
        <IssueSidePanel onClose={onClose}>
          <IssueViewer
            itemIdentifier={sidePanelItemIdentifier}
            optionConfig={{
              ...ISSUE_VIEWER_DEFAULT_CONFIG,
              shouldSkipSetDocumentTitle: true,
              onClose,
              insideSidePanel: true,
              // Note: onSubIssueClick/onParentIssueActivate/onIssueDelete are intentionally
              // omitted as navigating sub-issues from similar issues preview is not supported yet
              onIssueUpdate: noop,
              onRemovedFromProject: noop,
              navigateBack: onClose,
              additionalHeaderActions: (
                <IconButton
                  as="a"
                  variant="invisible"
                  icon={ScreenFullIcon}
                  aria-label={LABELS.sidePanelTooltip}
                  href={sidePanelItemURL}
                  target="_blank"
                />
              ),
            }}
          />
        </IssueSidePanel>
      )}
    </>
  )
}

try{ SimilarIssueSidePanel.displayName ||= 'SimilarIssueSidePanel' } catch {}